import React, { useMemo, useState } from "react";
import WebLayout from "../components/WebLayout";
import Seo from "../components/Seo";
import TurnstileWidget from "../components/TurnstileWidget";
import { Loader2, ArrowRight } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const TS_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const SELLER_TYPES = [
  { id: "existing_location", label: "Existing Vending Location", sub: "Placement/location only. Machine is not included." },
  { id: "turnkey",           label: "Turnkey Location + Machine", sub: "Existing placement with vending machine included." },
  { id: "route",             label: "Vending Route",              sub: "Multiple active vending locations." },
];

const LOC_TYPES = ["warehouse","manufacturing","office","hotel","apartment","gym","trucking/logistics","healthcare","school","other"];
const MACHINE_TYPES = ["snack","drink","combo","smart cooler","coffee","other"];

const yn = (v) => (v === true ? "true" : v === false ? "false" : "");

export default function Sell() {
  const [f, setF] = useState({
    first_name:"", last_name:"", email:"", phone:"", state:"",
    seller_type:"existing_location",
    asset_city:"", asset_state:"", asset_zip:"",
    location_type:"", vending_operating_duration:"",
    machine_currently_installed:null,
    approx_monthly_gross_sales:"", commission_rev_share:"",
    manager_aware_of_sale:"",
    machine_type:"", machine_brand_model:"", machine_age_years:"",
    cashless_reader:null, asking_price:"",
    route_num_locations:"", route_num_machines:"", route_cities_states:"", route_total_monthly_gross:"",
    reason_for_selling:"", additional_notes:"",
  });
  const [tsToken, setTsToken] = useState("");
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [banner, setBanner] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const tsRequired = !!TS_SITE_KEY;
  const showExisting = f.seller_type === "existing_location" || f.seller_type === "turnkey";
  const showTurnkey  = f.seller_type === "turnkey";
  const showRoute    = f.seller_type === "route";

  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setBool = (k, v) => () => setF({ ...f, [k]: v });

  async function submit(e) {
    e.preventDefault();
    if (tsRequired && !tsToken) { setBanner("Please complete the verification challenge."); return; }
    setLoading(true); setBanner("");
    const num = (v) => (v === "" || v === null ? null : Number(v));
    const payload = {
      first_name: f.first_name, last_name: f.last_name, email: f.email, phone: f.phone, state: f.state,
      seller_type: f.seller_type,
      asset_city: f.asset_city, asset_state: f.asset_state, asset_zip: f.asset_zip,
      location_type: showExisting ? f.location_type : null,
      vending_operating_duration: showExisting ? f.vending_operating_duration : null,
      machine_currently_installed: showExisting ? f.machine_currently_installed : null,
      approx_monthly_gross_sales: showExisting ? num(f.approx_monthly_gross_sales) : null,
      commission_rev_share: showExisting ? (f.commission_rev_share || null) : null,
      manager_aware_of_sale: showExisting ? (f.manager_aware_of_sale || null) : null,
      machine_type: showTurnkey ? f.machine_type : null,
      machine_brand_model: showTurnkey ? (f.machine_brand_model || null) : null,
      machine_age_years: showTurnkey ? num(f.machine_age_years) : null,
      cashless_reader: showTurnkey ? f.cashless_reader : null,
      asking_price: (showTurnkey || showRoute) ? num(f.asking_price) : null,
      route_num_locations: showRoute ? num(f.route_num_locations) : null,
      route_num_machines: showRoute ? num(f.route_num_machines) : null,
      route_cities_states: showRoute ? (f.route_cities_states || null) : null,
      route_total_monthly_gross: showRoute ? num(f.route_total_monthly_gross) : null,
      reason_for_selling: f.reason_for_selling || null,
      additional_notes: f.additional_notes || null,
      turnstile_token: tsToken,
    };
    try {
      const r = await fetch(`${API}/api/preregister/seller`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (r.status === 200) { setSubmitted(true); }
      else {
        const j = await r.json().catch(() => ({}));
        setBanner(j?.message || j?.detail || `Please review the form. (HTTP ${r.status})`);
      }
    } catch { setBanner("Network error. Please try again."); }
    setLoading(false);
  }

  if (submitted) {
    return (
      <WebLayout>
        <Seo title="Seller early access" description="You're on the SpotBounty seller early-access list." path="/sell" />
        <section className="max-w-2xl mx-auto px-6 py-24">
          <div className="sb-card p-8" data-testid="sell-success">
            <div className="sb-eyebrow">Seller early access</div>
            <h1 className="sb-headline text-[36px] md:text-[44px] mt-3">YOU&apos;RE ON THE SELLER EARLY-ACCESS LIST</h1>
            <p className="mt-4 text-[#A1A1AA]">Thanks for telling us about your vending asset. SpotBounty will contact you as we open seller marketplace access.</p>
          </div>
        </section>
      </WebLayout>
    );
  }

  return (
    <WebLayout>
      <Seo title="Sell a vending location or route" description="Sell your existing vending location, turnkey placement, or route. Join SpotBounty's seller early-access list." path="/sell" />
      <section className="max-w-3xl mx-auto px-6 py-16">
        <div className="sb-eyebrow">Seller early access</div>
        <h1 className="sb-headline text-[40px] md:text-[52px] mt-3">SELL YOUR VENDING LOCATION OR ROUTE</h1>
        <p className="mt-4 text-[#A1A1AA] max-w-2xl">Have an existing vending location, machine placement, or route you may want to sell? Tell us about it and join SpotBounty&apos;s seller early-access list.</p>

        <form onSubmit={submit} className="mt-10 grid gap-8" data-testid="sell-form">
          <div className="sb-card p-6">
            <div className="sb-eyebrow mb-3">What are you interested in selling?</div>
            <div className="grid gap-3">
              {SELLER_TYPES.map((o) => {
                const active = f.seller_type === o.id;
                return (
                  <button
                    type="button" key={o.id}
                    onClick={() => setF({ ...f, seller_type: o.id })}
                    data-testid={`sell-type-${o.id}`}
                    className={`text-left p-4 rounded-[12px] border ${active ? "border-[#FFC107] bg-[#181410]" : "border-[#2A2F34] hover:border-[#3a3f44]"}`}
                  >
                    <div className={`font-bold ${active ? "text-[#FFC107]" : ""}`}>{o.label}</div>
                    <div className="text-[13px] text-[#A1A1AA] mt-1">{o.sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Seller info */}
          <div className="sb-card p-6">
            <div className="sb-eyebrow mb-3">Your info</div>
            <div className="grid gap-3 md:grid-cols-2">
              <input required placeholder="First name" className="sb-input" value={f.first_name} onChange={set("first_name")} data-testid="sell-first-name" />
              <input required placeholder="Last name"  className="sb-input" value={f.last_name}  onChange={set("last_name")}  data-testid="sell-last-name" />
              <input required type="email" placeholder="Email" className="sb-input" value={f.email} onChange={set("email")} data-testid="sell-email" />
              <input required placeholder="Phone" className="sb-input" value={f.phone} onChange={set("phone")} data-testid="sell-phone" />
              <select required className="sb-input md:col-span-2" value={f.state} onChange={set("state")} data-testid="sell-state">
                <option value="">Your state</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>

          {/* Asset location */}
          <div className="sb-card p-6">
            <div className="sb-eyebrow mb-3">Asset location</div>
            <div className="grid gap-3 md:grid-cols-3">
              <input required placeholder="City" className="sb-input" value={f.asset_city} onChange={set("asset_city")} data-testid="sell-asset-city" />
              <select required className="sb-input" value={f.asset_state} onChange={set("asset_state")} data-testid="sell-asset-state">
                <option value="">State</option>
                {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <input required placeholder="ZIP" maxLength={5} className="sb-input" value={f.asset_zip} onChange={set("asset_zip")} data-testid="sell-asset-zip" />
            </div>
          </div>

          {showExisting && (
            <div className="sb-card p-6">
              <div className="sb-eyebrow mb-3">Location details</div>
              <div className="grid gap-3 md:grid-cols-2">
                <select required={showExisting} className="sb-input" value={f.location_type} onChange={set("location_type")} data-testid="sell-location-type">
                  <option value="">Location type</option>
                  {LOC_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input required={showExisting} placeholder="How long has vending been operating there? (e.g. 2 years)" className="sb-input" value={f.vending_operating_duration} onChange={set("vending_operating_duration")} data-testid="sell-duration" />
                <div className="flex items-center gap-3 text-[13px]">
                  <span className="text-[#A1A1AA]">Machine currently installed?</span>
                  <button type="button" onClick={setBool("machine_currently_installed", true)}  className={`px-3 py-1 rounded ${f.machine_currently_installed === true  ? "bg-[#FFC107] text-black" : "border border-[#2A2F34]"}`} data-testid="sell-installed-yes">Yes</button>
                  <button type="button" onClick={setBool("machine_currently_installed", false)} className={`px-3 py-1 rounded ${f.machine_currently_installed === false ? "bg-[#FFC107] text-black" : "border border-[#2A2F34]"}`} data-testid="sell-installed-no">No</button>
                </div>
                <input placeholder="Approx monthly gross sales (optional)" type="number" min="0" className="sb-input" value={f.approx_monthly_gross_sales} onChange={set("approx_monthly_gross_sales")} data-testid="sell-gross" />
                <input placeholder="Commission / revenue share, if any (optional)" className="sb-input md:col-span-2" value={f.commission_rev_share} onChange={set("commission_rev_share")} data-testid="sell-commission" />
                <select required={showExisting} className="sb-input md:col-span-2" value={f.manager_aware_of_sale} onChange={set("manager_aware_of_sale")} data-testid="sell-manager-aware">
                  <option value="">Is the property/business manager aware of a possible transfer/sale?</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                  <option value="not_yet">Not yet</option>
                </select>
              </div>
            </div>
          )}

          {showTurnkey && (
            <div className="sb-card p-6">
              <div className="sb-eyebrow mb-3">Machine details</div>
              <div className="grid gap-3 md:grid-cols-2">
                <select required={showTurnkey} className="sb-input" value={f.machine_type} onChange={set("machine_type")} data-testid="sell-machine-type">
                  <option value="">Machine type</option>
                  {MACHINE_TYPES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                <input placeholder="Brand / model (optional)" className="sb-input" value={f.machine_brand_model} onChange={set("machine_brand_model")} data-testid="sell-brand-model" />
                <input placeholder="Machine age in years (optional)" type="number" step="0.5" min="0" className="sb-input" value={f.machine_age_years} onChange={set("machine_age_years")} data-testid="sell-age" />
                <div className="flex items-center gap-3 text-[13px]">
                  <span className="text-[#A1A1AA]">Cashless reader?</span>
                  <button type="button" onClick={setBool("cashless_reader", true)}  className={`px-3 py-1 rounded ${f.cashless_reader === true  ? "bg-[#FFC107] text-black" : "border border-[#2A2F34]"}`} data-testid="sell-cashless-yes">Yes</button>
                  <button type="button" onClick={setBool("cashless_reader", false)} className={`px-3 py-1 rounded ${f.cashless_reader === false ? "bg-[#FFC107] text-black" : "border border-[#2A2F34]"}`} data-testid="sell-cashless-no">No</button>
                </div>
                <div className="md:col-span-2 text-[12px] text-[#71717A]">Machine included in sale.</div>
                <input placeholder="Approx asking price (optional)" type="number" min="0" className="sb-input md:col-span-2" value={f.asking_price} onChange={set("asking_price")} data-testid="sell-asking" />
              </div>
            </div>
          )}

          {showRoute && (
            <div className="sb-card p-6">
              <div className="sb-eyebrow mb-3">Route details</div>
              <div className="grid gap-3 md:grid-cols-2">
                <input required={showRoute} placeholder="Number of locations" type="number" min="1" className="sb-input" value={f.route_num_locations} onChange={set("route_num_locations")} data-testid="sell-route-locs" />
                <input required={showRoute} placeholder="Number of machines"  type="number" min="1" className="sb-input" value={f.route_num_machines}  onChange={set("route_num_machines")}  data-testid="sell-route-machines" />
                <textarea required={showRoute} placeholder="Cities / states served" className="sb-input md:col-span-2 min-h-[80px]" value={f.route_cities_states} onChange={set("route_cities_states")} data-testid="sell-route-cities" />
                <input placeholder="Total monthly gross sales (optional)" type="number" min="0" className="sb-input" value={f.route_total_monthly_gross} onChange={set("route_total_monthly_gross")} data-testid="sell-route-gross" />
                <input placeholder="Asking price (optional)" type="number" min="0" className="sb-input" value={f.asking_price} onChange={set("asking_price")} data-testid="sell-route-asking" />
              </div>
            </div>
          )}

          <div className="sb-card p-6">
            <div className="sb-eyebrow mb-3">Anything else</div>
            <div className="grid gap-3">
              <textarea placeholder="Why are you considering selling? (optional)" className="sb-input min-h-[70px]" value={f.reason_for_selling} onChange={set("reason_for_selling")} data-testid="sell-reason" />
              <textarea placeholder="Anything else we should know? (optional)" className="sb-input min-h-[70px]" value={f.additional_notes} onChange={set("additional_notes")} data-testid="sell-notes" />
            </div>
          </div>

          <div className="sb-card p-6 text-[13.5px] text-[#A1A1AA] leading-relaxed grid gap-3">
            <div>Submitting this form does not create a listing or guarantee a sale. SpotBounty is currently collecting seller interest for future marketplace access.</div>
            <div>Financial figures submitted during early access are seller-provided and have not been independently verified by SpotBounty.</div>
          </div>

          {tsRequired && (
            <TurnstileWidget siteKey={TS_SITE_KEY} onToken={setTsToken} />
          )}

          <label htmlFor="sell-consent" className="flex items-start gap-2 text-[12px] text-[#A1A1AA] leading-relaxed cursor-pointer">
            <input id="sell-consent" type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} data-testid="sell-consent" className="mt-[3px] w-4 h-4 accent-[#FFC107] shrink-0" required />
            <span>I agree to the <a href="/terms" className="text-[#FFC107] underline">Terms of Service</a> and acknowledge the <a href="/privacy" className="text-[#FFC107] underline">Privacy Policy</a>.</span>
          </label>

          {banner && <div className="text-[13px] text-[#EF4444]" data-testid="sell-banner">{banner}</div>}

          <button type="submit" disabled={loading || (tsRequired && !tsToken) || !consent} data-testid="sell-submit" className="sb-btn-primary w-full disabled:opacity-50">
            {loading ? <><Loader2 size={16} className="animate-spin" /> Sending…</> : <>Join Seller Early Access <ArrowRight size={16} /></>}
          </button>
        </form>
      </section>
    </WebLayout>
  );
}
